import React, { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CloseIcon from '@mui/icons-material/Close';

import TopBar from '../../../../../components/utils/TopBar';
import * as api from '../../../../../api/index';

const TYPE_COLORS = {
  SUBCHAPTER:   { bg: '#1e3a5f', border: '#2563eb', label: '#93c5fd' },
  NOTE:         { bg: '#1a3326', border: '#16a34a', label: '#86efac' },
  SUBDIVISION:  { bg: '#3b2f1a', border: '#d97706', label: '#fcd34d' },
  SUBPROVISION: { bg: '#2d1a3b', border: '#9333ea', label: '#c4b5fd' },
  TABLE:        { bg: '#1a2e3b', border: '#0891b2', label: '#67e8f9' },
};

// How this node's text compares with the previous revision. Carried on the
// (node, revision) cross-reference, so it is a property of the placement —
// the same text can read NEW at one node and UNCHANGED at another.
const DELTA_COLORS = {
  NEW:             { fg: '#86efac', bd: '#16a34a' },
  UNCHANGED:       { fg: '#64748b', bd: '#334155' },
  CONTENT_CHANGED: { fg: '#fcd34d', bd: '#d97706' },
  DELETED:         { fg: '#fca5a5', bd: '#dc2626' },
};

const NodeBox = ({ node, depth, onViewContent }) => {
  const [expanded, setExpanded] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const hasContent = !!node.content;
  const colors = TYPE_COLORS[node.node_type] || TYPE_COLORS.SUBCHAPTER;
  const delta = node.content?.delta_status;
  const deltaColors = DELTA_COLORS[delta];
  const isDeleted = node.content?.is_deleted;

  return (
    <Box sx={{ ml: depth > 0 ? '24px' : 0, mb: '6px' }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '6px',
        p: '8px 12px', borderRadius: '8px',
        background: colors.bg, border: `1px solid ${colors.border}`,
        opacity: isDeleted ? 0.5 : 1,
        transition: 'all 0.15s',
        '&:hover': { borderColor: '#fff', background: `${colors.bg}dd` },
      }}>
        {hasChildren ? (
          <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ p: '2px', color: '#94a3b8' }}>
            {expanded ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        ) : (
          <Box sx={{ width: 26 }} />
        )}

        <Chip
          label={node.node_type}
          size="small"
          sx={{
            height: 20, fontSize: 10, fontWeight: 700,
            background: 'transparent', color: colors.label,
            border: `1px solid ${colors.border}`,
            '& .MuiChip-label': { px: '6px' },
          }}
        />

        <Typography variant="body2" fontWeight={600} color="#e2e8f0"
          sx={{ flex: 1, textDecoration: isDeleted ? 'line-through' : 'none' }}>
          {node.node_type === 'SUBCHAPTER' ? `Subchapter ${node.label}` :
           node.node_type === 'NOTE' ? `Note ${node.label}` :
           node.node_type === 'TABLE' ? node.label :
           node.label}
        </Typography>

        {deltaColors && (
          <Tooltip
            placement="top"
            title={
              delta === 'UNCHANGED' ? 'Text identical to the previous revision — stored once, referenced here'
              : delta === 'NEW' ? 'This node first carries text in this revision'
              : delta === 'CONTENT_CHANGED' ? 'Wording differs from the previous revision'
              : 'Present in the previous revision, absent from this one'
            }
          >
            <Chip
              label={delta}
              size="small"
              sx={{
                height: 18, fontSize: 9, fontWeight: 700, flexShrink: 0,
                background: 'transparent', color: deltaColors.fg,
                border: `1px solid ${deltaColors.bd}`,
                '& .MuiChip-label': { px: '6px' },
              }}
            />
          </Tooltip>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
          <Tooltip
            placement="top"
            title={
              <Box>
                {(node.node_label_path || '').split('/').map((seg, idx) => {
                  const types = ['SUBCHAPTER', 'NOTE', 'SUBDIVISION', 'SUBPROVISION'];
                  const type = types[idx] || 'NODE';
                  return (
                    <Typography key={idx} variant="caption" display="block" sx={{ fontSize: 11, lineHeight: 1.6 }}>
                      <span style={{ color: '#94a3b8' }}>{type}:</span>{' '}
                      <span style={{ color: '#fff', fontWeight: 600 }}>{seg}</span>
                    </Typography>
                  );
                })}
              </Box>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'default' }}>
              <Typography variant="caption" color="#e2e8f0" sx={{ fontSize: 9 }}>path</Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
                {node.node_label_path}
              </Typography>
            </Box>
          </Tooltip>

          {hasChildren && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography variant="caption" color="#e2e8f0" sx={{ fontSize: 9 }}>children</Typography>
              <Chip
                label={node.children.length}
                size="small"
                sx={{ height: 18, fontSize: 10, fontWeight: 700, background: '#334155', color: '#94a3b8', '& .MuiChip-label': { px: '5px' } }}
              />
            </Box>
          )}

          {hasContent && node.content.page_from != null && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography variant="caption" color="#e2e8f0" sx={{ fontSize: 9 }}>pp.</Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: 10 }}>
                {node.content.page_from}
                {node.content.page_to && node.content.page_to !== node.content.page_from
                  ? `-${node.content.page_to}` : ''}
              </Typography>
            </Box>
          )}

          {hasContent && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Typography variant="caption" color="#e2e8f0" sx={{ fontSize: 9 }}>chars</Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: 10 }}>
                {node.content.char_count?.toLocaleString()}
              </Typography>
            </Box>
          )}
        </Box>

        {hasContent && (
          <Tooltip title="View raw text" placement="top">
            <IconButton size="small" onClick={() => onViewContent(node)} sx={{ p: '3px', color: '#60a5fa' }}>
              <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {!hasContent && node.node_type !== 'SUBCHAPTER' && (
          <Typography variant="caption" color="#475569" sx={{ fontSize: 9 }}>no content</Typography>
        )}
      </Box>

      {hasChildren && (
        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ mt: '4px', borderLeft: `1px solid ${colors.border}33`, ml: '12px', pl: '4px' }}>
            {node.children.map((child) => (
              <NodeBox key={child.id} node={child} depth={depth + 1} onViewContent={onViewContent} />
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const ScNodeVisualizerPage = () => {
  const [loading, setLoading] = useState(true);
  const [revisions, setRevisions] = useState([]);
  const [selectedRevision, setSelectedRevision] = useState('');
  const [tree, setTree] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalNode, setModalNode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [viewMode, setViewMode] = useState('raw');

  useEffect(() => {
    fetchTree();
  }, []); // eslint-disable-line

  const fetchTree = async (revisionId) => {
    setLoading(true);
    try {
      const { data, status } = await api.getScNodeTreeBIZcoreTD(revisionId || '');
      if (status === 200 && data.data) {
        setRevisions(data.data.revisions || []);
        setSelectedRevision(data.data.selected_revision || '');
        setTree(data.data.tree || []);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleRevisionChange = (e) => {
    setSelectedRevision(e.target.value);
    fetchTree(e.target.value);
  };

  const handleViewContent = (node) => {
    setModalNode(node);
    setModalOpen(true);
    setCopied(false);
    const text = node?.content?.raw_legal_text || '';
    let isJson = false;
    try { const p = JSON.parse(text); isJson = p && p.rows; } catch (e) {}
    const hasMarkers = text.includes('[LIST]') || text.includes('[TABLE_DATA]');
    setViewMode((isJson || hasMarkers) ? 'table' : 'raw');
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const selectedRevObj = revisions.find(r => r.id === selectedRevision);

  return (
    <Box>
      <TopBar title={"SC Node Visualizer"} />

      <Box sx={{
        background: '#0f1117',
        borderRadius: '8px',
        border: '1px solid #1e293b',
        minHeight: 'calc(100vh - 140px)',
        overflow: 'auto',
        p: '20px',
      }}>
        {/* Revision selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', mb: '20px' }}>
          <TextField
            select size="small" label="Revision"
            value={selectedRevision}
            onChange={handleRevisionChange}
            sx={{
              minWidth: 300,
              '& .MuiInputBase-root': { color: '#e2e8f0', background: '#1e293b' },
              '& .MuiInputLabel-root': { color: '#64748b' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
            }}>
            {revisions.map((r) => (
              <MenuItem key={r.id} value={r.id}>{r.name} ({r.effective_date})</MenuItem>
            ))}
          </TextField>

          {selectedRevObj && (
            <Box>
              <Typography variant="caption" color="#64748b">
                Effective: {selectedRevObj.effective_date}
              </Typography>
            </Box>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: '60px' }}>
            <CircularProgress size={28} sx={{ color: '#3b82f6' }} />
          </Box>
        ) : tree.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: '60px' }}>
            <Typography variant="body2" color="#475569">No nodes found for this revision.</Typography>
          </Box>
        ) : (
          <Box>
            {tree.map((sc) => (
              <NodeBox key={sc.id} node={sc} depth={0} onViewContent={handleViewContent} />
            ))}
          </Box>
        )}
      </Box>

      {/* Raw text modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box className="flexCenterCenterRow" width="100vw" height="100vh">
          <Box sx={{
            width: '92vw', maxWidth: 1200, maxHeight: '85vh',
            background: '#1a1b23', borderRadius: '12px', border: '1px solid #334155',
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Modal header */}
            <Box sx={{ px: '20px', py: '14px', background: '#0f1117', borderBottom: '1px solid #1e293b',
                       display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" fontWeight={700} color="#e2e8f0">
                  {modalNode?.node_label_path}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  {modalNode?.node_type} — {modalNode?.content?.char_count?.toLocaleString()} characters
                  {modalNode?.content?.page_from != null && (
                    <> · page {modalNode.content.page_from}
                      {modalNode.content.page_to && modalNode.content.page_to !== modalNode.content.page_from
                        ? `-${modalNode.content.page_to}` : ''}</>
                  )}
                  {modalNode?.content?.effective_from && (
                    <> · effective {modalNode.content.effective_from}</>
                  )}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setModalOpen(false)} sx={{ color: '#64748b' }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Hash row — the hash IS the identity of the stored text, so two
                nodes or two revisions showing the same hash are reading one
                shared row rather than two copies. */}
            {modalNode?.content && (
              <Box sx={{ px: '20px', py: '8px', background: '#111827', borderBottom: '1px solid #1e293b',
                         display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography variant="caption" color="#475569">SHA-256:</Typography>
                <Typography variant="caption" color="#94a3b8" sx={{ fontFamily: 'monospace', fontSize: 11 }}>
                  {modalNode.content.text_hash}
                </Typography>
                <Tooltip title={copied ? "Copied" : "Copy hash"}>
                  <IconButton size="small" onClick={() => handleCopy(modalNode.content.text_hash)} sx={{ p: '2px', color: '#475569' }}>
                    <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
                {modalNode.content.delta_status && (
                  <Chip
                    label={modalNode.content.delta_status}
                    size="small"
                    sx={{
                      height: 18, fontSize: 9, fontWeight: 700, ml: 'auto',
                      background: 'transparent',
                      color: (DELTA_COLORS[modalNode.content.delta_status] || {}).fg || '#64748b',
                      border: `1px solid ${(DELTA_COLORS[modalNode.content.delta_status] || {}).bd || '#334155'}`,
                      '& .MuiChip-label': { px: '6px' },
                    }}
                  />
                )}
              </Box>
            )}

            {/* Content body */}
            <Box sx={{ flex: 1, overflow: 'auto', p: '20px' }}>
              {(() => {
                const rawText = modalNode?.content?.raw_legal_text || '';
                let parsed = null;
                try { parsed = JSON.parse(rawText); } catch (e) { /* not JSON */ }
                const isJson = parsed && parsed.rows && Array.isArray(parsed.rows);

                if (isJson && viewMode === 'table') {
                  const colLabels = {
                    code: 'HTS Code', stat_suffix: 'Stat', description: 'Description',
                    general_rate: 'General', special_rate: 'Special', col2_rate: 'Col 2',
                    effective_period: 'Effective Period', page: 'Page',
                    heading: 'HTS Code', rate_general: 'General', rate_special: 'Special',
                    rate_col2: 'Col 2', unit_of_qty: 'UOM',
                  };
                  const cols = parsed.columns || Object.keys(parsed.rows[0] || {});
                  const colStyles = {
                    code:             { minWidth: 110, fontWeight: 600, color: '#60a5fa', whiteSpace: 'nowrap' },
                    heading:          { minWidth: 110, fontWeight: 600, color: '#60a5fa', whiteSpace: 'nowrap' },
                    stat_suffix:      { minWidth: 40, textAlign: 'center', whiteSpace: 'nowrap' },
                    description:      { minWidth: 280, maxWidth: 450, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5 },
                    unit_of_qty:      { minWidth: 60, whiteSpace: 'nowrap', textAlign: 'center' },
                    general_rate:     { minWidth: 80, whiteSpace: 'nowrap' },
                    special_rate:     { minWidth: 80, whiteSpace: 'nowrap' },
                    col2_rate:        { minWidth: 70, whiteSpace: 'nowrap' },
                    rate_general:     { minWidth: 80, whiteSpace: 'nowrap' },
                    rate_special:     { minWidth: 80, whiteSpace: 'nowrap' },
                    rate_col2:        { minWidth: 70, whiteSpace: 'nowrap' },
                    effective_period: { minWidth: 130, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.5 },
                    page:             { minWidth: 45, textAlign: 'center', whiteSpace: 'nowrap' },
                  };
                  return (
                    <Box sx={{ overflow: 'auto' }}>
                      <Table size="small" sx={{
                        tableLayout: 'fixed',
                        width: 'max-content',
                        minWidth: '100%',
                        '& th': { color: '#94a3b8', borderColor: '#1e293b', fontSize: 11, px: '10px', py: '8px', fontWeight: 700, position: 'sticky', top: 0, background: '#1a1b23', zIndex: 1, whiteSpace: 'nowrap' },
                        '& td': { color: '#cbd5e1', borderColor: '#1e293b', fontSize: 11, px: '10px', py: '6px', verticalAlign: 'top' },
                      }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 40 }}>#</TableCell>
                            {cols.map((col) => (
                              <TableCell key={col} sx={{ width: colStyles[col]?.minWidth || 'auto' }}>
                                {colLabels[col] || col}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {parsed.rows.map((row, ri) => (
                            <TableRow key={ri} sx={{ '&:hover': { background: '#1e293b' } }}>
                              <TableCell sx={{ color: '#475569' }}>{ri + 1}</TableCell>
                              {cols.map((col) => (
                                <TableCell key={col} sx={colStyles[col] || {}}>
                                  {row[col] ?? ''}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  );
                }

                if (viewMode === 'raw') {
                  return (
                    <Typography variant="body2" sx={{
                      color: '#cbd5e1', fontFamily: 'monospace', fontSize: 12,
                      lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {rawText || 'No content available.'}
                    </Typography>
                  );
                }

                // Formatted view: render [LIST] and [TABLE_DATA] markers visually
                const parts = rawText.split(/(\[LIST\].*?\[\/LIST\]|\[TABLE_DATA\].*?\[\/TABLE_DATA\])/gs);
                return (
                  <Box>
                    {parts.map((part, pi) => {
                      const listMatch = part.match(/^\[LIST\](.*?)\[\/LIST\]$/s);
                      if (listMatch) {
                        const items = listMatch[1].split(' | ');
                        return (
                          <Box key={pi} sx={{ my: '8px', p: '10px', background: '#111827', borderRadius: '6px', border: '1px solid #1e293b' }}>
                            <Typography variant="caption" color="#475569" mb="6px" display="block">List ({items.length} items)</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {items.map((item, ii) => (
                                <Chip key={ii} label={item.trim()} size="small"
                                  sx={{ height: 24, fontSize: 11, background: '#1e293b', color: '#e2e8f0', border: '1px solid #334155', '& .MuiChip-label': { px: '8px' } }} />
                              ))}
                            </Box>
                          </Box>
                        );
                      }

                      const tableMatch = part.match(/^\[TABLE_DATA\](.*?)\[\/TABLE_DATA\]$/s);
                      if (tableMatch) {
                        try {
                          const rows = JSON.parse(tableMatch[1]);
                          const cols = Object.keys(rows[0] || {});
                          return (
                            <Box key={pi} sx={{ my: '8px', background: '#111827', borderRadius: '6px', border: '1px solid #1e293b', overflow: 'auto' }}>
                              <Typography variant="caption" color="#475569" px="10px" pt="8px" display="block">Embedded data ({rows.length} rows)</Typography>
                              <Table size="small" sx={{
                                '& th': { color: '#94a3b8', borderColor: '#1e293b', fontSize: 11, px: '10px', py: '4px', fontWeight: 700 },
                                '& td': { color: '#e2e8f0', borderColor: '#1e293b', fontSize: 11, px: '10px', py: '3px', fontFamily: 'monospace' },
                              }}>
                                <TableHead>
                                  <TableRow>
                                    {cols.map(c => <TableCell key={c}>{c}</TableCell>)}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {rows.map((r, ri) => (
                                    <TableRow key={ri} sx={{ '&:hover': { background: '#1e293b' } }}>
                                      {cols.map(c => <TableCell key={c}>{r[c] ?? ''}</TableCell>)}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          );
                        } catch (e) {
                          return <Typography key={pi} variant="body2" sx={{ color: '#fca5a5', fontFamily: 'monospace', fontSize: 12 }}>{part}</Typography>;
                        }
                      }

                      if (!part.trim()) return null;
                      return (
                        <Typography key={pi} variant="body2" sx={{
                          color: '#cbd5e1', fontFamily: 'monospace', fontSize: 12,
                          lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        }}>
                          {part}
                        </Typography>
                      );
                    })}
                  </Box>
                );
              })()}
            </Box>

            {/* Modal footer */}
            <Box sx={{ px: '20px', py: '10px', borderTop: '1px solid #1e293b',
                       display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: '4px' }}>
                {(() => {
                  return (
                    <>
                      <Tooltip title="Raw text">
                        <IconButton size="small" onClick={() => setViewMode('raw')}
                          sx={{ color: viewMode === 'raw' ? '#60a5fa' : '#475569', border: viewMode === 'raw' ? '1px solid #334155' : 'none', borderRadius: '6px', p: '4px' }}>
                          <CodeOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Table view">
                        <IconButton size="small" onClick={() => setViewMode('table')}
                          sx={{ color: viewMode === 'table' ? '#60a5fa' : '#475569', border: viewMode === 'table' ? '1px solid #334155' : 'none', borderRadius: '6px', p: '4px' }}>
                          <TableChartOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  );
                })()}
              </Box>
              <Box sx={{ display: 'flex', gap: '8px' }}>
                <Button
                  size="small" variant="outlined"
                  onClick={() => handleCopy(modalNode?.content?.raw_legal_text || '')}
                  sx={{ color: '#94a3b8', borderColor: '#334155', fontSize: 12 }}>
                  {copied ? 'Copied' : 'Copy text'}
                </Button>
                <Button size="small" variant="outlined"
                  onClick={() => setModalOpen(false)}
                  sx={{ color: '#94a3b8', borderColor: '#334155', fontSize: 12 }}>
                  Close
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ScNodeVisualizerPage;
